/**
 * @file Utilities for using Cloudflare Turnstile to check if a user is human
 * Reference: https://developers.cloudflare.com/turnstile/
 */
import environment from '@@/bits/environment'
import { loadJs } from '@@/bits/load'

// Don't use global turnstile outside of this file
declare global {
  interface Window {
    turnstile: Turnstile
  }
}

interface TurnstileOptions {
  // https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/#configurations
  sitekey: string
  action?: string
  cData?: Record<string, unknown>
  callback?: (token: string) => void
  'expired-callback'?: () => void
  'timeout-callback'?: () => void
  'error-callback'?: (errorCode?: string) => void
  theme?: 'light' | 'dark' | 'auto'
  tabindex?: number
  size?: 'normal' | 'flexible' | 'compact'
  appearance?: 'always' | 'execute' | 'interaction-only'
  execution?: 'render' | 'execute'
  'refresh-expired'?: 'auto' | 'manual' | 'never'
  'refresh-timeout'?: 'auto' | 'manual' | 'never'
  retry?: 'auto' | 'never'
  'retry-interval'?: number
  language?: string
}

interface Turnstile {
  // returns widgetId (string) on successful call and undefined on failure
  render: (selectorOrHTMLElement: string | HTMLElement, options: TurnstileOptions) => string | undefined
  reset: (widgetId?: string) => void
  getResponse: (widgetId: string) => string | null | undefined
  remove: (widgetId: string) => void
  isExpired: (widgetId: string) => boolean
}

// Should be okay to hard code site key here since it's not a secret and needs to be available in the frontend
// For none production environment, we can test using these site keys (Reference: https://developers.cloudflare.com/turnstile/frequently-asked-questions/#are-there-sitekeys-and-secret-keys-that-can-be-used-for-testing):
//   - 1x00000000000000000000AA: always pass
//   - 2x00000000000000000000AB: always block
//   - 3x00000000000000000000FF: force interactive challenge (for non invisible widget)
const SITE_KEY = environment === 'production' ? '0x4AAAAAAACTVvAGayAtbpXb' : '1x00000000000000000000AA'

// load script through our permacache proxy
const SCRIPT_URL = 'https://rf.padletcdn.com/challenges.cloudflare.com/turnstile/v0/api.js'

interface LoadTurnstileOptions {
  explicitRendering: boolean
}
// Load Cloudflare Turnstile scripts, should only run this once for each page
async function loadTurnstile({ explicitRendering }: LoadTurnstileOptions): Promise<Turnstile> {
  await loadJs(`${SCRIPT_URL}${explicitRendering ? '?render=explicit' : ''}`)
  return window.turnstile
}

async function loadInvisibleTurnstile(options: Omit<TurnstileOptions, 'sitekey'>): Promise<string | undefined> {
  const dummyId = `cf-turnstile-dummy-${Date.now()}`
  const dummyDiv = document.createElement('div')
  dummyDiv.id = dummyId
  document.body.appendChild(dummyDiv)

  const turnstile = await loadTurnstile({ explicitRendering: true })

  const renderOptions: TurnstileOptions = { ...options, sitekey: SITE_KEY }

  // Cloudflare's token expires every 5 minutes: https://developers.cloudflare.com/turnstile/frequently-asked-questions/#how-long-is-a-turnstile-token-valid-for-before-it-is-rejected-by-siteverify
  // If no special instruction is set for when the the token expired/timeout, just try to get a new token
  if (renderOptions['expired-callback'] == null) {
    renderOptions['expired-callback'] = () => {
      turnstile.reset()
    }
  }
  if (renderOptions['timeout-callback'] == null) {
    renderOptions['timeout-callback'] = () => {
      turnstile.reset()
    }
  }

  return turnstile.render(`#${dummyId}`, renderOptions)
}

// Should only be called after turnstile is loaded to get the human verification token
// widgetId can the id selector of the parent element of turnstile or th widget id returned by turnstile.render/loadInvisibleTurnstile
function getTurnstileVerificationToken(widgetId: string | null): string | null | undefined {
  if (widgetId === null) {
    return null
  }
  return window.turnstile.getResponse(widgetId)
}

// Reset turnstile to get a new token
function resetTurnstile(widgetId: string): void {
  if (widgetId == null) {
    return
  }
  window.turnstile.reset(widgetId)
}

// Direct Cloudflare URL — Cloudflare warns that api.js must NOT be proxied or cached.
// Used by managed widget; the existing SCRIPT_URL proxy is kept for the invisible widget to avoid regression.
const DIRECT_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js'

interface ManagedTurnstileOptions {
  siteKey: string
  container: string | HTMLElement
  action?: string
  theme?: 'light' | 'dark' | 'auto'
  language?: string
  onVerify: (token: string) => void
  onError?: (errorCode?: string) => void
  onExpire?: () => void
  onTimeout?: () => void
}

async function loadManagedTurnstile(): Promise<Turnstile> {
  await loadJs(`${DIRECT_SCRIPT_URL}?render=explicit`)
  return window.turnstile
}

async function renderManagedTurnstile(options: ManagedTurnstileOptions): Promise<string | undefined> {
  const turnstile = await loadManagedTurnstile()

  const renderOptions: TurnstileOptions = {
    sitekey: options.siteKey,
    action: options.action,
    theme: options.theme ?? 'auto',
    size: 'flexible',
    appearance: 'always',
    execution: 'render',
    'refresh-expired': 'auto',
    'refresh-timeout': 'auto',
    retry: 'auto',
    language: options.language ?? 'auto',
    callback: options.onVerify,
    'error-callback': options.onError,
    'expired-callback': options.onExpire,
    'timeout-callback': options.onTimeout,
  }

  return turnstile.render(options.container, renderOptions)
}

function removeTurnstile(widgetId: string): void {
  if (widgetId == null) return
  window.turnstile.remove(widgetId)
}

export {
  getTurnstileVerificationToken,
  loadInvisibleTurnstile,
  removeTurnstile,
  renderManagedTurnstile,
  resetTurnstile,
}
export type { ManagedTurnstileOptions }
